/**
 * @author Ricardo Comendador Escalona
 * @version v0.0.2
 * @date  15/11/2021
 */
import React, {memo, useState, useCallback, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import styles from './../TemplateDetailsAuditor/index.less';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import { Card, Input } from 'antd';
import MapPinLineIcon from 'remixicon-react/MapPinLineIcon';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import {useCrudRunAudit} from '@/modules/auditorAudits/hooks/useAuditorAudits';
import {useParams} from 'react-router-dom';
import {useGetOneCountry, useGetOneState} from '@/hooks/useGeoPlaces';

const TemplateDetailsLocation = ({question, runAudit}) => {
  const {t} = useTranslation('templates');
  const [data, setData] = useState('');
  const {currentAudit} = useAuditData();
  const {data: province, isLoading: isLoadingProvince} = useGetOneState(currentAudit?.establishment?.province);
  const {data: country, isLoading: isLoadingCountry} = useGetOneCountry(currentAudit?.establishment?.country);

  // Construir la dirección a partir de los datos del establecimiento
  const getAddress = useMemo(() => {
    if (isLoadingProvince || isLoadingCountry) {
      return '';
    }
    const address = currentAudit?.establishment?.address;
    const parts = [address, province?.name].filter(Boolean);

    if (country?.name) {
      parts.push(country.name);
    }

    return parts.join(', ');
  }, [country?.name, currentAudit?.establishment?.address, isLoadingCountry, isLoadingProvince, province?.name]);

  // Obtener el valor guardado previamente
  const savedValue = useMemo(() =>
    currentAudit?.coverFields?.find(x => x?._id === question?._id)?.value,
  [currentAudit?.coverFields, question?._id]);

  // Valor actual a mostrar (prioridad: valor guardado > dirección generada)
  const currentValue = useMemo(() =>
    savedValue || getAddress,
  [savedValue, getAddress]);

  // Efecto para establecer el valor inicial SOLO una vez
  useEffect(() => {
    // Solo establecer el valor inicial si el estado está vacío
    if (!data) {
      if (savedValue) {
        setData(savedValue);
      } else if (getAddress) {
        setData(getAddress);
      }
    }
    // eslint-disable-next-line
  }, [savedValue, getAddress]); // Removido 'data' de las dependencias

  // Efecto separado para actualizar cuando cambie el valor guardado externamente
  useEffect(() => {
    if (savedValue && savedValue !== data) {
      setData(savedValue);
    }
    // eslint-disable-next-line
  }, [savedValue]); // Solo depende de savedValue

  const {createMetadataValue} = useCrudRunAudit();
  const {id} = useParams();

  const onSave = useCallback(async () => {
    if (runAudit) {
      const coverFields = {
        _id: question?._id,
        value: data
      };

      await createMetadataValue({auditId: id, coverFields});
    }
  }, [createMetadataValue, data, id, question?._id, runAudit]);

  return (
    <Card
      className={classNames(styles.templateAuditorCard, styles.templateLocationCard)}
    >
      <div className="mb-3">
        {question?.name || question?.label}
      </div>
      <div className="custom-form-big">
        {runAudit ? (
          <Input
            value={data}
            onChange={e => setData(e?.target?.value)}
            onPressEnter={e => {
              e.preventDefault();
              e.stopPropagation();
              if (runAudit) {
                onSave().then();
              }
            }}
            onBlur={onSave}
            placeholder={t('details.placeholderLocation')}
            suffix={
              <div className={'flex flex-row justify-center items-center'}>
                <MapPinLineIcon size="18px" color="rgba(0, 0, 0, 0.25)" />
              </div>
            }
          />
        ) : (
          <Input
            value={currentValue}
            readOnly
          />
        )}
      </div>
    </Card>
  );
};

TemplateDetailsLocation.propTypes = {
  question: PropTypes.object,
  isCover: PropTypes.bool,
  runAudit: PropTypes.bool,
};

TemplateDetailsLocation.defaultProps = {

};

export default memo(TemplateDetailsLocation);

/**
 * cellRender
 * */
/**
 * This is not a components,
 * it is a function to render the Detail Audit Card components in a detail drawer
 * @param {String} value this is the value of the dataIndex of the table (we will ignored in this case)
 * @param {Object} entity this is the row instance
 * */
export const renderTemplateDetailsLocation = (value, entity) => {
  return (
    <TemplateDetailsLocation entity={entity}/>
  );
};